import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useContract } from '../../hooks/useQueries'
import { Can } from '../../contexts/PermissionsContext'
import { ContractClausesTab } from '../ContractClausesTab'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { AppIcon } from '../ui/icon'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { CollapsibleText } from './CollapsibleText'
import { CoverageSummary } from './CoverageSummary'
import { ResolutionAssets } from './ResolutionAssets'
import { VoteBar } from './VoteBar'
import { subjectIcon } from './assembly-icons'
import {
    apiErrorMessage,
    useAssemblyAttendance,
    useAssemblyChildren,
    useAuthorizedContracts,
    useContractCoverage,
    useAssemblyMutations,
    useAssemblyOutcome,
    useAssemblyVotes,
} from './assembly-api'
import {
    DEFAULT_REASONS,
    RECORDED_OUTCOMES,
    VOTES,
    buildVotePayload,
    describeComputed,
    describeMembers,
    clauseText,
    needsOverrideReason,
    outcomeTone,
    resolutionTitle,
    ruleShort,
    type VoteDraft,
} from './assembly-utils'

/** One contract that runs under the resolution, with whether the resolution covers it. */
function AuthorizedContractRow({ org, contract }: { org: string; contract: { uid: string; code: string; name?: string } }) {
    const { data: coverage } = useContractCoverage(org, contract.uid)
    return (
        <li className="flex flex-wrap items-center justify-between gap-2" data-testid={`authorizes-${contract.uid}`}>
            <Link
                to="/$org/contracts/$contractUid"
                params={{ org, contractUid: contract.uid }}
                className="font-medium underline-offset-2 hover:underline"
                data-testid={`authorizes-link-${contract.uid}`}
            >
                {contract.name || contract.code}
            </Link>
            <CoverageSummary coverage={coverage as never} testId={`authorizes-coverage-${contract.uid}`} />
        </li>
    )
}

/**
 * One resolution, in the order a manager reads it: its title and facts, the
 * vote result (what the engine computes and what the minutes record), what it
 * authorizes, the votes of the sheet, and last the legal text and the clause
 * editor, both folded. Spec 2026-10-05-01 §6, §7, §8; spec 2026-10-10-08 §8.
 */
export function ResolutionPanel({
    org,
    minutesUid,
    resolutionUid,
    closed,
    entryLocked = false,
}: {
    org: string
    minutesUid: string
    resolutionUid: string
    closed: boolean
    /** Votes and outcome are not typed here: the meeting is not held yet, or the sitting votes. */
    entryLocked?: boolean
}) {
    const { t } = useTranslation('assemblies')
    const locked = closed || entryLocked
    const { data: resolution } = useContract(org, resolutionUid, ['contract_type'])
    const { data: sheet } = useAssemblyAttendance(org, minutesUid)
    const { data: votes } = useAssemblyVotes(org, resolutionUid)
    const { data: outcome } = useAssemblyOutcome(org, resolutionUid)
    const { putVotes, setOutcome } = useAssemblyMutations(org)
    const path = { orgCode: org, contractUid: resolutionUid }

    // The draft starts from the saved votes and is re-seeded when they change,
    // without dropping a vote picked before they arrived: the panel is open
    // while `GET /votes` is still in flight, and a reset there erased the pick
    // and saved an incomplete sheet (weekly e2e run, issue #1565). The panel is
    // keyed by resolution, so a draft never leaks into the next one.
    const saved = useMemo(() => {
        const m: Record<string, VoteDraft> = {}
        for (const v of votes?.votes ?? []) m[v.user_uid] = { vote: v.vote, defaultReason: v.default_reason ?? '' }
        return m
    }, [votes])
    const [drafts, setDrafts] = useState<Record<string, VoteDraft>>({})
    useEffect(() => setDrafts((prev) => ({ ...saved, ...prev })), [saved])
    const [incomplete, setIncomplete] = useState<string[]>([])

    const [recorded, setRecorded] = useState('')
    const [reason, setReason] = useState('')
    useEffect(() => {
        setRecorded(outcome?.recorded_outcome ?? '')
        setReason(outcome?.outcome_override_reason ?? '')
    }, [outcome?.recorded_outcome, outcome?.outcome_override_reason])

    const saveVotes = () => {
        const { votes: payload, incomplete: missing } = buildVotePayload(drafts)
        setIncomplete(missing)
        if (missing.length > 0 || payload.length === 0) return
        putVotes.mutate({ params: { path }, body: { votes: payload } })
    }
    const reasonRequired = needsOverrideReason(outcome, recorded)
    const saveOutcome = () =>
        setOutcome.mutate({
            params: { path },
            body: { recorded_outcome: recorded as (typeof RECORDED_OUTCOMES)[number], outcome_override_reason: reason },
        })

    const voteError = apiErrorMessage(putVotes.error)
    const outcomeError = apiErrorMessage(setOutcome.error)
    const computed = outcome?.computed
    const { data: authorized } = useAuthorizedContracts(org, resolutionUid)
    const { data: kids } = useAssemblyChildren(org, minutesUid)
    const child = (kids?.data ?? []).find((c) => c.uid === resolutionUid)
    const resolutionClauses = child?.clauses
    const title = child ? resolutionTitle(child) : ''
    const proposedAssets = Array.isArray(resolutionClauses?.concerned_asset_uids) ? (resolutionClauses?.concerned_asset_uids as string[]) : []
    const rule = clauseText(resolutionClauses, 'majority_rule')
    const subject = clauseText(resolutionClauses, 'resolution_subject')
    const legal = clauseText(resolutionClauses, 'resolution_text')

    return (
        <div className="space-y-4" data-testid="resolution-panel">
            <Card data-testid="resolution-header">
                <CardContent className="space-y-2 p-4">
                    <h3 className="text-lg font-semibold" data-testid="resolution-panel-title">{title}</h3>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        {subject && (
                            <span className="inline-flex items-center gap-1">
                                <AppIcon path={subjectIcon(subject)} size="xs" tone="muted" />
                                {t(`subject.${subject}`, { defaultValue: subject })}
                            </span>
                        )}
                        {ruleShort(rule) && <Badge variant="outline" title={t(`rule.${rule}`)}>{ruleShort(rule)}</Badge>}
                        {outcome?.recorded_outcome && (
                            <Badge variant={outcomeTone(outcome.recorded_outcome)} data-testid="resolution-panel-outcome">
                                {t(`outcome.${outcome.recorded_outcome}`)}
                            </Badge>
                        )}
                    </div>
                </CardContent>
            </Card>

            <Card data-testid="outcome-card">
                <CardHeader>
                    <CardTitle className="text-base">{t('panel.outcome')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    {computed && (
                        <VoteBar
                            voicesFor={computed.voices_for}
                            voicesAgainst={computed.voices_against}
                            voicesAbstain={computed.voices_abstain}
                            testId="outcome-bar"
                        />
                    )}
                    {computed ? (
                        <div className="space-y-1 text-sm" data-testid="outcome-computed">
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('panel.computed')}</span>
                                <Badge variant={outcomeTone(computed.result)} data-testid="outcome-computed-result">
                                    {t(`outcome.${computed.result}`)}
                                </Badge>
                                <span className="tabular-nums" data-testid="outcome-computed-detail">{describeComputed(computed)}</span>
                            </div>
                            {describeMembers(computed) && (
                                <div className="text-muted-foreground" data-testid="outcome-members">{t('panel.members', { detail: describeMembers(computed) })}</div>
                            )}
                            {computed.bridge && (
                                <div className="text-muted-foreground" data-testid="outcome-bridge">{t('panel.bridge', { rule: t(`rule.${computed.bridge}`) })}</div>
                            )}
                            {computed.rule_version && <div className="text-xs text-muted-foreground">{t('panel.rule_version', { version: computed.rule_version })}</div>}
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground" data-testid="outcome-not-computed">{outcome?.computed_error || t('panel.not_computed')}</p>
                    )}

                    {entryLocked && !closed && (
                        <p className="text-sm text-muted-foreground" data-testid="entry-locked-hint">{t('panel.entry_locked')}</p>
                    )}
                    <div>
                        <Label htmlFor="recorded-outcome">{t('panel.recorded')}</Label>
                        <select
                            id="recorded-outcome"
                            value={recorded}
                            disabled={locked}
                            onChange={(e) => setRecorded(e.target.value)}
                            className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="outcome-recorded-select"
                        >
                            <option value="">{t('panel.recorded_unset')}</option>
                            {RECORDED_OUTCOMES.map((o) => <option key={o} value={o}>{t(`outcome.${o}`)}</option>)}
                        </select>
                    </div>
                    {(reasonRequired || outcome?.diverges || reason) && (
                        <div>
                            <Label htmlFor="override-reason">{t('panel.override_reason')}</Label>
                            <Textarea id="override-reason" value={reason} disabled={locked} onChange={(e) => setReason(e.target.value)} data-testid="outcome-override-reason" />
                            {reasonRequired && !reason.trim() && <p className="text-sm text-destructive" data-testid="outcome-reason-required">{t('panel.override_reason_required')}</p>}
                        </div>
                    )}
                    {outcomeError && <p className="text-sm text-destructive" data-testid="outcome-error">{outcomeError}</p>}
                    <Can perm="contracts.write">
                        <Button size="sm" onClick={saveOutcome} disabled={locked || !recorded || setOutcome.isPending || (reasonRequired && !reason.trim())} data-testid="outcome-save">
                            {t('panel.save_outcome')}
                        </Button>
                    </Can>
                </CardContent>
            </Card>

            <ResolutionAssets
                org={org}
                minutesUid={minutesUid}
                resolutionUid={resolutionUid}
                proposed={proposedAssets}
                closed={closed}
            />

            <Card data-testid="resolution-authorizes">
                <CardHeader>
                    <CardTitle className="text-base">{t('panel.authorizes')}</CardTitle>
                </CardHeader>
                <CardContent>
                    {(authorized?.data ?? []).length === 0 ? (
                        <p className="text-sm text-muted-foreground" data-testid="resolution-authorizes-empty">{t('panel.authorizes_none')}</p>
                    ) : (
                        <ul className="space-y-2 text-sm">
                            {(authorized?.data ?? []).map((c) => <AuthorizedContractRow key={c.uid} org={org} contract={c} />)}
                        </ul>
                    )}
                </CardContent>
            </Card>

            <Card data-testid="votes-card">
                <CardHeader>
                    <CardTitle className="text-base">{t('panel.votes')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    {outcome?.votes_aggregate_only && (
                        <p className="text-sm text-muted-foreground" data-testid="votes-aggregate-only">{t('panel.aggregate_only')}</p>
                    )}
                    {(sheet?.parties ?? []).length === 0 ? (
                        <p className="text-sm text-muted-foreground" data-testid="votes-no-sheet">{t('panel.no_sheet')}</p>
                    ) : (
                        <div className="divide-y rounded-md border" data-testid="votes-table">
                            {(sheet?.parties ?? []).map((p) => {
                                const d = drafts[p.user_uid] ?? { vote: '', defaultReason: '' }
                                const set = (next: Partial<VoteDraft>) =>
                                    setDrafts((prev) => ({ ...prev, [p.user_uid]: { ...d, ...next } }))
                                return (
                                    <div key={p.user_uid} className="grid grid-cols-[1fr_5rem_9rem_14rem] items-center gap-3 px-3 py-2" data-testid={`vote-row-${p.user_uid}`}>
                                        <div className="truncate">{p.user_name || p.user_uid}</div>
                                        <div className="text-right tabular-nums">{p.voices}</div>
                                        <select
                                            aria-label={t('panel.vote')}
                                            value={d.vote}
                                            disabled={locked}
                                            onChange={(e) => set({ vote: e.target.value as VoteDraft['vote'] })}
                                            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                                            data-testid={`vote-select-${p.user_uid}`}
                                        >
                                            <option value="">{t('panel.vote_unset')}</option>
                                            {VOTES.map((v) => <option key={v} value={v}>{t(`vote.${v}`)}</option>)}
                                        </select>
                                        {d.vote === 'defaulting' ? (
                                            <select
                                                aria-label={t('panel.default_reason')}
                                                value={d.defaultReason}
                                                disabled={locked}
                                                onChange={(e) => set({ defaultReason: e.target.value })}
                                                className={`h-8 rounded-md border bg-background px-2 text-sm ${incomplete.includes(p.user_uid) ? 'border-destructive' : 'border-input'}`}
                                                data-testid={`vote-reason-${p.user_uid}`}
                                            >
                                                <option value="">{t('panel.default_reason_unset')}</option>
                                                {DEFAULT_REASONS.map((r) => <option key={r} value={r}>{t(`default_reason.${r}`)}</option>)}
                                            </select>
                                        ) : <span />}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                    {incomplete.length > 0 && <p className="text-sm text-destructive" data-testid="votes-incomplete">{t('panel.default_reason_required')}</p>}
                    {voteError && <p className="text-sm text-destructive" data-testid="votes-error">{voteError}</p>}
                    <Can perm="contracts.write">
                        <Button size="sm" onClick={saveVotes} disabled={locked || putVotes.isPending || (sheet?.parties ?? []).length === 0} data-testid="votes-save">
                            {t('panel.save_votes')}
                        </Button>
                    </Can>
                </CardContent>
            </Card>

            <Card data-testid="resolution-legal">
                <CardContent className="space-y-3 p-4">
                    <CollapsibleText
                        text={legal}
                        testId="resolution-legal-text"
                        label={{ show: t('panel.show_legal_text'), hide: t('panel.hide_legal_text') }}
                    />
                    {!legal && <p className="text-sm text-muted-foreground" data-testid="resolution-legal-empty">{t('panel.no_legal_text')}</p>}
                    <Collapsible>
                        <CollapsibleTrigger className="text-xs font-medium text-primary underline-offset-2 hover:underline" data-testid="resolution-clauses-toggle">
                            {t('panel.edit_clauses')}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-3" data-testid="resolution-clauses">
                    {resolution && (
                        <ContractClausesTab
                            org={org}
                            contractUid={resolutionUid}
                            contractTypeUid={
                                (resolution as { contract_type?: { uid?: string }; contract_type_uid?: string }).contract_type?.uid ||
                                (resolution as { contract_type_uid?: string }).contract_type_uid ||
                                ''
                            }
                        />
                    )}
                        </CollapsibleContent>
                    </Collapsible>
                </CardContent>
            </Card>
        </div>
    )
}
